import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { Download, BookOpen, ArrowUpRight, ArrowDownRight, IndianRupee, RefreshCw } from 'lucide-react';
import useProtectedExport from '../hooks/useProtectedExport';
import ExportPasswordDialog from '../components/ExportPasswordDialog';

const SupplierLedger = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [ledgerData, setLedgerData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const protectedExport = useProtectedExport();
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await api.get('/suppliers/');
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Failed to fetch suppliers');
    }
  }, []);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  const fetchLedger = async () => {
    if (!selectedSupplier) {
      toast.error('Please select a supplier');
      return;
    }

    setLoading(true);
    try {
      let url = `/supplier-ledger/${selectedSupplier}`;
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      if (params.toString()) url += `?${params.toString()}`;

      const [ledgerRes, summaryRes] = await Promise.all([
        api.get(url),
        api.get(`/supplier-ledger/${selectedSupplier}/summary`)
      ]);
      
      setLedgerData(ledgerRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch ledger');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    if (!selectedSupplier) return;

    try {
      let url = `/supplier-ledger/${selectedSupplier}/export`;
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await api.get(url, { responseType: 'blob' });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      const supplierName = suppliers.find(s => s.id === selectedSupplier)?.name || 'supplier';
      link.setAttribute('download', `ledger_${supplierName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      toast.success('Ledger exported as CSV');
    } catch (error) {
      toast.error('Failed to export ledger');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const clearFilters = () => {
    setFromDate('');
    setToDate('');
    if (selectedSupplier) {
      fetchLedger();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Supplier Ledger</h1>
          <p className="text-gray-600">View supplier account statement (Indian format)</p>
        </div>
        {ledgerData && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => protectedExport.requestExport(handleExportCSV)}>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button variant="outline" onClick={() => protectedExport.requestExport(handlePrint)}>
              <Download className="h-4 w-4 mr-2" />
              Print / PDF
            </Button>
          </div>
        )}
      </div>

      {/* Supplier Selection and Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-5 gap-4">
            <div className="col-span-2">
              <Label>Select Supplier *</Label>
              <Select
                value={selectedSupplier || 'none'}
                onValueChange={(v) => {
                  setSelectedSupplier(v === 'none' ? '' : v);
                  setLedgerData(null);
                  setSummary(null);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a supplier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Choose a supplier</SelectItem>
                  {suppliers.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>From Date</Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div>
              <Label>To Date</Label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={fetchLedger} disabled={!selectedSupplier || loading}>
                {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <BookOpen className="h-4 w-4 mr-2" />}
                Load Ledger
              </Button>
              <Button variant="outline" onClick={clearFilters}>
                Clear
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <ArrowUpRight className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Purchases</p>
                  <p className="text-xl font-bold">₹{summary.total_purchases?.toLocaleString('en-IN')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <ArrowDownRight className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Payments</p>
                  <p className="text-xl font-bold">₹{summary.total_payments?.toLocaleString('en-IN')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${summary.balance_type === 'Payable' ? 'bg-amber-100' : summary.balance_type === 'Advance' ? 'bg-blue-100' : 'bg-green-100'}`}>
                  <IndianRupee className={`h-6 w-6 ${summary.balance_type === 'Payable' ? 'text-amber-600' : summary.balance_type === 'Advance' ? 'text-blue-600' : 'text-green-600'}`} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Current Balance</p>
                  <p className={`text-xl font-bold ${summary.balance_type === 'Payable' ? 'text-amber-600' : summary.balance_type === 'Advance' ? 'text-blue-600' : 'text-green-600'}`}>
                    ₹{Math.abs(summary.current_balance)?.toLocaleString('en-IN')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <BookOpen className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <Badge variant={
                    summary.balance_type === 'Payable' ? 'destructive' : 
                    summary.balance_type === 'Advance' ? 'secondary' : 'default'
                  }>
                    {summary.balance_type}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Ledger Table */}
      {ledgerData && (
        <Card className="print-section">
          <CardHeader className="border-b">
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  Ledger Statement
                </CardTitle>
                <p className="text-lg font-semibold mt-2">{ledgerData.supplier?.name}</p>
                {(fromDate || toDate) && (
                  <p className="text-sm text-gray-500">
                    Period: {fromDate || 'Beginning'} to {toDate || 'Today'}
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Opening Balance</p>
                <p className="text-lg font-semibold">₹{ledgerData.opening_balance?.toLocaleString('en-IN')}</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-[100px]">Date</TableHead>
                  <TableHead>Particulars</TableHead>
                  <TableHead className="text-right w-[130px]">Debit (₹)</TableHead>
                  <TableHead className="text-right w-[130px]">Credit (₹)</TableHead>
                  <TableHead className="text-right w-[130px]">Balance (₹)</TableHead>
                  <TableHead className="w-[120px]">Reference</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ledgerData.opening_balance !== 0 && (
                  <TableRow className="bg-blue-50">
                    <TableCell>-</TableCell>
                    <TableCell className="font-medium">Opening Balance</TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell className="text-right font-medium">
                      {ledgerData.opening_balance?.toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                )}
                {ledgerData.entries?.length > 0 ? (
                  ledgerData.entries.map((entry, index) => (
                    <TableRow key={index} className={entry.debit > 0 ? 'bg-green-50/30' : 'bg-red-50/30'}>
                      <TableCell className="text-sm">
                        {new Date(entry.date).toLocaleDateString('en-IN')}
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{entry.particulars}</span>
                        {entry.notes && (
                          <p className="text-xs text-gray-500">{entry.notes}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-green-700 font-medium">
                        {entry.debit > 0 ? entry.debit.toLocaleString('en-IN') : ''}
                      </TableCell>
                      <TableCell className="text-right text-red-700 font-medium">
                        {entry.credit > 0 ? entry.credit.toLocaleString('en-IN') : ''}
                      </TableCell>
                      <TableCell className={`text-right font-medium ${entry.balance >= 0 ? 'text-amber-600' : 'text-blue-600'}`}>
                        {entry.balance?.toLocaleString('en-IN')}
                        <span className="text-xs ml-1">{entry.balance >= 0 ? 'Cr' : 'Dr'}</span>
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {entry.reference || '-'}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                      No transactions found for selected period
                    </TableCell>
                  </TableRow>
                )}
                
                {/* Totals Row */}
                {ledgerData.entries?.length > 0 && (
                  <>
                    <TableRow className="bg-gray-100 font-semibold border-t-2">
                      <TableCell colSpan={2} className="text-right">Total:</TableCell>
                      <TableCell className="text-right text-green-700">
                        {ledgerData.total_debit?.toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell className="text-right text-red-700">
                        {ledgerData.total_credit?.toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                    <TableRow className="bg-blue-100 font-bold">
                      <TableCell colSpan={2} className="text-right">Closing Balance:</TableCell>
                      <TableCell colSpan={2}></TableCell>
                      <TableCell className={`text-right ${ledgerData.closing_balance >= 0 ? 'text-amber-700' : 'text-blue-700'}`}>
                        ₹{Math.abs(ledgerData.closing_balance)?.toLocaleString('en-IN')}
                        <span className="text-sm ml-1">{ledgerData.closing_balance >= 0 ? '(Payable)' : '(Advance)'}</span>
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Print styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print-section, .print-section * {
            visibility: visible;
          }
          .print-section {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
        }
      `}</style>
      <ExportPasswordDialog {...protectedExport} />
    </div>
  );
};

export default SupplierLedger;
